import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ActionUpdateById } from '../../../store/Actions/CommonAction';
import {
    prepareChangedSubmissionData,
    splitPayloadByDateChunks,
    groupMedicinesByDate,
} from '../utils/dataTransformers';

/**
 * Maximum number of dates allowed per API call to avoid payload-too-large errors.
 */
const MAX_DATES_PER_CHUNK = 15;

/**
 * Dispatch a single API update and return a Promise.
 * Centralizes the dispatch-to-promise conversion used by both
 * single and chunked submissions.
 *
 * @param {Function} dispatch - Redux dispatch
 * @param {string} patientPlanId - Patient plan ID
 * @param {Object} payload - Data to send
 * @returns {Promise<Object>} Resolves with the API response
 */
const dispatchUpdate = (dispatch, patientPlanId, payload) => {
    return new Promise((resolve, reject) => {
        dispatch(
            ActionUpdateById(
                {
                    Query: `admin/patient-plan/${patientPlanId}`,
                    obj: payload,
                },
                (response) => {
                    if (response?.statusCode === 200) {
                        resolve(response);
                    } else {
                        reject(response);
                    }
                }
            )
        );
    });
};

/**
 * Describe a chunk for user-facing error messages.
 * Extracts the date range from the chunk payload.
 *
 * @param {Object} chunk - Payload chunk
 * @param {number} index - 0-based index
 * @param {number} total - Total number of chunks
 * @returns {string}
 */
const describeChunk = (chunk, index, total) => {
    const dateKeyedFields = ['serviceDetails', 'medicine', 'dietPlan', 'doctorRoundForm'];
    const dates = new Set();
    dateKeyedFields.forEach(field => {
        if (chunk[field] && typeof chunk[field] === 'object') {
            Object.keys(chunk[field]).forEach(k => {
                if (/^\d{4}-\d{2}-\d{2}$/.test(k)) dates.add(k);
            });
        }
    });
    const sortedDates = Array.from(dates).sort();
    if (sortedDates.length === 0) return `Chunk ${index + 1}/${total}`;
    return `Chunk ${index + 1}/${total} (${sortedDates[0]} → ${sortedDates[sortedDates.length - 1]})`;
};

/**
 * Custom hook to handle form submission
 * Supports automatic payload splitting for large date ranges (15+ dates)
 * with sequential API calls and proper error handling.
 *
 * @param {string} patientPlanId - ID of the patient plan
 * @param {Object} patientPlanData - Service details data
 * @param {Function} setError - Error setter function
 * @param {Object} currentPlanData - Existing patient plan data for diffing
 * @returns {Object} Form submission handler
 */
export const useFormSubmission = (patientPlanId, patientPlanData, setError, currentPlanData = null) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /**
     * Main form submission handler.
     * 1. Diffs current form values against existing data (date-level diffing).
     * 2. If there are more than MAX_DATES_PER_CHUNK changed dates, splits
     *    the payload into smaller chunks and sends them sequentially.
     * 3. Handles API failures gracefully — stops on first failure and shows
     *    the user which chunk failed.
     */
    const handleSubmit = useCallback(async (values, { setErrors, setSubmitting }) => {
        try {
            // Step 1: Prepare diff payload (only changed dates)
            const finalValue = prepareChangedSubmissionData(values, patientPlanData, currentPlanData);
            setSubmitting(true);
            setError(null);

            if (!finalValue || Object.keys(finalValue).length === 0) {
                setSubmitting(false);
                navigate(-1);
                return;
            }

            // Step 2: Split into chunks if payload has too many dates
            const chunks = splitPayloadByDateChunks(finalValue, MAX_DATES_PER_CHUNK);

            if (chunks.length === 0) {
                setSubmitting(false);
                navigate(-1);
                return;
            }

            // Step 3: Single chunk — straightforward API call
            if (chunks.length === 1) {
                try {
                    await dispatchUpdate(dispatch, patientPlanId, chunks[0]);
                    setError(null);
                    navigate(-1);
                } catch (response) {
                    if (response?.statusCode === 400) {
                        setErrors(response?.data?.errors || {});
                        setError('Validation failed. Please check the form.');
                    } else {
                        setError('Failed to save patient plan. Please try again.');
                    }
                } finally {
                    setSubmitting(false);
                }
                return;
            }

            // Step 4: Multiple chunks — sequential API calls with progress
            console.log(`[FormSubmission] Large payload detected. Splitting into ${chunks.length} chunks.`);

            for (let i = 0; i < chunks.length; i++) {
                const chunkLabel = describeChunk(chunks[i], i, chunks.length);
                console.log(`[FormSubmission] Sending ${chunkLabel}...`);

                try {
                    await dispatchUpdate(dispatch, patientPlanId, chunks[i]);
                    console.log(`[FormSubmission] ✅ ${chunkLabel} saved successfully.`);
                } catch (response) {
                    // Stop on first failure — report which chunk failed
                    const completedCount = i;
                    const remainingCount = chunks.length - i;

                    if (response?.statusCode === 400) {
                        setErrors(response?.data?.errors || {});
                        setError(
                            `Validation failed at ${chunkLabel}. ` +
                            `${completedCount} chunk(s) saved, ${remainingCount} remaining. ` +
                            `Please fix errors and try again.`
                        );
                    } else {
                        setError(
                            `Failed to save ${chunkLabel}. ` +
                            `${completedCount}/${chunks.length} chunk(s) were saved successfully. ` +
                            `Please try again to save the remaining data.`
                        );
                    }
                    setSubmitting(false);
                    return;
                }
            }

            // All chunks saved successfully
            setError(null);
            navigate(-1);
        } catch (err) {
            console.error('Error in form submission:', err);
            setError('Failed to submit form. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [dispatch, navigate, patientPlanId, patientPlanData, currentPlanData, setError]);

    /**
     * Handle submission of a specific round type only.
     * These are typically small payloads (single date + round type),
     * so chunking is not needed here.
     *
     * @param {Object} values - Form values
     * @param {string} date - Date of the round
     * @param {string} roundType - Type of round (morning/evening/emergency)
     */
    const handleSubmitSpecificRound = useCallback((values, date, roundType) => {
        try {
            const finalValue = prepareChangedSubmissionData(values, patientPlanData, currentPlanData, roundType);
            setError(null);

            if (!finalValue || Object.keys(finalValue).length === 0) {
                return Promise.resolve({ statusCode: 200, message: 'No changes detected' });
            }

            return dispatchUpdate(dispatch, patientPlanId, finalValue);
        } catch (err) {
            console.error('Error in form submission:', err);
            setError('Failed to submit form. Please try again.');
            return Promise.reject(err);
        }
    }, [dispatch, patientPlanId, patientPlanData, currentPlanData, setError]);

    /**
     * Submit only medications without any diffing or filtering of unmodified dates.
     * Groups form medicines by date and sends `{ medicine: groupedMedicines }` to the backend.
     *
     * @param {Object} values - Form values
     * @param {Object} formikBag - Formik action helpers
     */
    const handleSubmitMedicines = useCallback(
        async (values, { setErrors, setSubmitting }) => {
            try {
                setSubmitting(true);
                setError(null);

                // Group all medicines by date without any diffing against current backend state
                const medicinesGrouped = groupMedicinesByDate(values.medicines);

                const payload = {
                    medicine: medicinesGrouped,
                };

                try {
                    await dispatchUpdate(dispatch, patientPlanId, payload);
                    setError(null);
                    navigate(-1);
                } catch (response) {
                    if (response?.statusCode === 400) {
                        setErrors(response?.data?.errors || {});
                        setError('Validation failed. Please check the form.');
                    } else {
                        setError('Failed to save medications. Please try again.');
                    }
                } finally {
                    setSubmitting(false);
                }
            } catch (err) {
                console.error('Error in medicine submission:', err);
                setError('Failed to submit medications. Please try again.');
                setSubmitting(false);
            }
        },
        [dispatch, navigate, patientPlanId, setError]
    );

    return {
        handleSubmit,
        handleSubmitSpecificRound,
        handleSubmitMedicines,
    };
};
